import { checkIsLoop } from "@/core/util";
import { getLoopStackForStep } from "@/flow/utils/loopUtils";

export type EdgeLike = {
  source?: any;
  target?: any;
  sourceHandle?: any;
  targetHandle?: any;
  hidden?: any;
  data?: any;
};

type OutputKey = "output" | "metaoutput";

type NormalizedEdge = {
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
};

type RouteDefinition = {
  token: string;
  routeId: string;
  firstStepOrigId: string;
  stepOrigIds: Set<string>;
};

type RouterDefinition = {
  origId: string;
  routes: RouteDefinition[];
};

type RouteReach = {
  reachedBranchTokens: Set<string>;
};

type GraphOrderContext = {
  graphOrderByOrigId: Map<string, number>;
  treeOrderByOrigId: Map<string, number>;
};

const graphOrderContextBySteps = new WeakMap<
  Array<any>,
  { edges: Array<EdgeLike>; context: GraphOrderContext }
>();

const normalizeKey = (value: any): string =>
  value !== undefined && value !== null ? String(value) : "";

const isEdgeHidden = (edge: EdgeLike): boolean => {
  if (!edge) return true;
  if (edge.hidden) return true;

  const { data } = edge;
  if (!data) return false;

  if (typeof data === "object") {
    return Boolean((data as any).hidden);
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return Boolean(parsed?.hidden);
    } catch {
      return false;
    }
  }

  return false;
};

const isLoopBackEdge = (edge: EdgeLike): boolean => {
  const sourceHandle = normalizeKey(edge?.sourceHandle);
  const targetHandle = normalizeKey(edge?.targetHandle);
  return (
    sourceHandle.includes("__handle-top") &&
    targetHandle.includes("__handle-top")
  );
};

const isSubset = (subset: Set<string>, superset: Set<string>): boolean =>
  Array.from(subset).every((item) => superset.has(item));

const addToMapSet = (
  map: Map<string, Set<string>>,
  key: string,
  value: string
) => {
  const values = map.get(key) ?? new Set<string>();
  values.add(value);
  map.set(key, values);
};

const collectStepOrigIds = (steps: any[]): Set<string> => {
  const ids = new Set<string>();

  const visit = (arr: any[]) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((step) => {
      const origId = normalizeKey(step?.origId);
      if (origId) ids.add(origId);
      if (Array.isArray(step?.routes)) {
        step.routes.forEach((route: any) => visit(route?.steps));
      }
      if (Array.isArray(step?.steps)) {
        visit(step.steps);
      }
    });
  };

  visit(steps);
  return ids;
};

const collectGraphContext = (steps: any[]) => {
  const allSteps: any[] = [];
  const stepByOrigId = new Map<string, any>();
  const origIdByAlias = new Map<string, string>();
  const treeOrderByOrigId = new Map<string, number>();
  const routers: RouterDefinition[] = [];

  const rememberStep = (step: any) => {
    const origId = normalizeKey(step?.origId);
    if (!origId || stepByOrigId.has(origId)) return;

    stepByOrigId.set(origId, step);
    origIdByAlias.set(origId, origId);

    const id = normalizeKey(step?.id);
    if (id && !origIdByAlias.has(id)) {
      origIdByAlias.set(id, origId);
    }

    treeOrderByOrigId.set(origId, treeOrderByOrigId.size);
    allSteps.push(step);
  };

  const visit = (arr: any[]) => {
    if (!Array.isArray(arr)) return;

    arr.forEach((step) => {
      if (!step) return;
      rememberStep(step);

      if (Array.isArray(step.routes)) {
        const routerOrigId = normalizeKey(step.origId);
        if (routerOrigId) {
          const routes = step.routes.map((route: any, index: number) => {
            const routeId = normalizeKey(route?.id);
            const firstStep = Array.isArray(route?.steps)
              ? route.steps[0]
              : null;
            return {
              token: routeId || `${routerOrigId}::route::${index}`,
              routeId,
              firstStepOrigId: normalizeKey(firstStep?.origId ?? firstStep?.id),
              stepOrigIds: collectStepOrigIds(route?.steps),
            };
          });
          routers.push({ origId: routerOrigId, routes });
        }

        step.routes.forEach((route: any) => visit(route?.steps));
      }

      if (Array.isArray(step.steps)) {
        visit(step.steps);
      }
    });
  };

  visit(steps);

  const routeByRouteId = new Map<string, RouteDefinition>();
  routers.forEach((router) => {
    router.routes.forEach((route) => {
      if (route.routeId) routeByRouteId.set(route.routeId, route);
    });
  });
  allSteps.forEach((step) => {
    const stepOrigId = normalizeKey(step?.origId);
    const route = routeByRouteId.get(normalizeKey(step?.parentId));
    if (stepOrigId && route) {
      route.stepOrigIds.add(stepOrigId);
      if (!route.firstStepOrigId) {
        route.firstStepOrigId = stepOrigId;
      }
    }
  });

  return {
    allSteps,
    stepByOrigId,
    origIdByAlias,
    treeOrderByOrigId,
    routers,
  };
};

const normalizeEdges = (
  edgeList: EdgeLike[],
  origIdByAlias: Map<string, string>
): NormalizedEdge[] => {
  if (!Array.isArray(edgeList)) return [];

  return edgeList.reduce<NormalizedEdge[]>((acc, edge) => {
    if (!edge || isEdgeHidden(edge) || isLoopBackEdge(edge)) return acc;

    const sourceRaw = normalizeKey(edge.source);
    const targetRaw = normalizeKey(edge.target);
    if (!sourceRaw || !targetRaw) return acc;
    if (sourceRaw === "start" || targetRaw === "start") return acc;

    const source = origIdByAlias.get(sourceRaw);
    const target = origIdByAlias.get(targetRaw);
    if (!source || !target || source === target) return acc;

    acc.push({
      source,
      target,
      sourceHandle: normalizeKey(edge.sourceHandle),
      targetHandle: normalizeKey(edge.targetHandle),
    });
    return acc;
  }, []);
};

const buildAdjacency = (edges: NormalizedEdge[]) => {
  const forward = new Map<string, Set<string>>();
  const reverse = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    addToMapSet(forward, edge.source, edge.target);
    addToMapSet(reverse, edge.target, edge.source);
  });

  return { forward, reverse };
};

const getAncestorOrigIdsFactory = (reverse: Map<string, Set<string>>) => {
  const cache = new Map<string, Set<string>>();

  const getAncestorOrigIds = (origId: string): Set<string> => {
    if (cache.has(origId)) return cache.get(origId) as Set<string>;

    const ancestors = new Set<string>();
    const visiting = new Set<string>([origId]);
    const walked = new Set<string>();

    const walk = (targetOrigId: string) => {
      const directSources = reverse.get(targetOrigId);
      if (!directSources) return;

      directSources.forEach((sourceOrigId) => {
        ancestors.add(sourceOrigId);
        if (walked.has(sourceOrigId)) return;

        const cached = cache.get(sourceOrigId);
        if (cached) {
          cached.forEach((cachedOrigId) => ancestors.add(cachedOrigId));
          walked.add(sourceOrigId);
          return;
        }

        if (visiting.has(sourceOrigId)) return;

        visiting.add(sourceOrigId);
        walk(sourceOrigId);
        visiting.delete(sourceOrigId);
        walked.add(sourceOrigId);
      });
    };

    walk(origId);
    ancestors.delete(origId);
    cache.set(origId, ancestors);
    return ancestors;
  };

  return getAncestorOrigIds;
};

const getRouteForEntryTarget = (
  targetOrigId: string,
  router: RouterDefinition,
  stepByOrigId: Map<string, any>
): RouteDefinition | null => {
  const targetStep = stepByOrigId.get(targetOrigId);
  const parentId = normalizeKey(targetStep?.parentId);
  const parentMatches = parentId
    ? router.routes.filter((route) => route.routeId === parentId)
    : [];
  const physicalMatches = router.routes.filter((route) =>
    route.stepOrigIds.has(targetOrigId)
  );

  if (parentMatches.length === 1) {
    if (
      physicalMatches.length === 0 ||
      physicalMatches.some((route) => route.token === parentMatches[0].token)
    ) {
      return parentMatches[0];
    }
    return null;
  }

  if (physicalMatches.length === 1) {
    return physicalMatches[0];
  }

  return null;
};

const addRouteReach = (
  routeReachByOrigId: Map<string, Map<string, RouteReach>>,
  origId: string,
  routerOrigId: string,
  branchToken: string
) => {
  const byRouter = routeReachByOrigId.get(origId) ?? new Map();
  const reach = byRouter.get(routerOrigId) ?? {
    reachedBranchTokens: new Set<string>(),
  };

  reach.reachedBranchTokens.add(branchToken);
  byRouter.set(routerOrigId, reach);
  routeReachByOrigId.set(origId, byRouter);
};

const buildRouteReachByOrigId = ({
  routers,
  edges,
  forward,
  stepByOrigId,
}: {
  routers: RouterDefinition[];
  edges: NormalizedEdge[];
  forward: Map<string, Set<string>>;
  stepByOrigId: Map<string, any>;
}): Map<string, Map<string, RouteReach>> => {
  const routeReachByOrigId = new Map<string, Map<string, RouteReach>>();

  routers.forEach((router) => {
    const seedsByToken = new Map<string, Set<string>>();
    const entryEdges = edges.filter((edge) => {
      if (edge.source !== router.origId) return false;
      if (edge.sourceHandle.includes("__handle-bottom")) return true;
      if (
        edge.sourceHandle.includes("__handle-right") ||
        edge.sourceHandle.includes("__handle-top")
      ) {
        return false;
      }
      return Boolean(getRouteForEntryTarget(edge.target, router, stepByOrigId));
    });

    entryEdges.forEach((edge, index) => {
      const route = getRouteForEntryTarget(edge.target, router, stepByOrigId);
      const token =
        route?.token ?? `${router.origId}::unknown::${edge.target}::${index}`;

      addToMapSet(seedsByToken, token, edge.target);
    });

    const walkSeeds = (seeds: Set<string>, token: string) => {
      const visited = new Set<string>();
      const stack = Array.from(seeds);

      while (stack.length > 0) {
        const current = stack.pop() as string;
        if (!visited.has(current)) {
          visited.add(current);

          addRouteReach(routeReachByOrigId, current, router.origId, token);

          const next = forward.get(current);
          if (next) {
            next.forEach((target) => {
              if (!visited.has(target)) stack.push(target);
            });
          }
        }
      }
    };

    seedsByToken.forEach((seeds, token) => {
      walkSeeds(seeds, token);
    });

    router.routes.forEach((route) => {
      if (seedsByToken.has(route.token)) return;

      if (route.firstStepOrigId) {
        walkSeeds(new Set([route.firstStepOrigId]), route.token);
      }
    });
  });

  return routeReachByOrigId;
};

const buildBoundarySourcesByTarget = (
  edges: NormalizedEdge[]
): Map<string, Set<string>> => {
  const sourcesByTargetHandle = new Map<
    string,
    { target: string; sources: Set<string> }
  >();

  edges.forEach((edge) => {
    const key = `${edge.target}\u0000${edge.targetHandle}`;
    const entry = sourcesByTargetHandle.get(key) ?? {
      target: edge.target,
      sources: new Set<string>(),
    };
    entry.sources.add(edge.source);
    sourcesByTargetHandle.set(key, entry);
  });

  const boundarySourcesByTarget = new Map<string, Set<string>>();
  sourcesByTargetHandle.forEach(({ target, sources }) => {
    if (sources.size <= 1) return;
    const existing = boundarySourcesByTarget.get(target) ?? new Set<string>();
    sources.forEach((source) => existing.add(source));
    boundarySourcesByTarget.set(target, existing);
  });

  return boundarySourcesByTarget;
};

const getBoundaryDirectSourcesToIgnore = (
  stepOrigId: string,
  getAncestorOrigIds: (origId: string) => Set<string>,
  boundarySourcesByTarget: Map<string, Set<string>>
): Set<string> => {
  const ignored = new Set<string>();
  const targets = new Set<string>([stepOrigId]);
  getAncestorOrigIds(stepOrigId).forEach((ancestor) => targets.add(ancestor));

  targets.forEach((target) => {
    const sources = boundarySourcesByTarget.get(target);
    if (!sources) return;
    sources.forEach((source) => ignored.add(source));
  });

  return ignored;
};

const getGraphOrder = (
  origIds: string[],
  edges: NormalizedEdge[],
  treeOrderByOrigId: Map<string, number>
): Map<string, number> => {
  const nodeSet = new Set(origIds);
  const indegree = new Map<string, number>();
  const outgoing = new Map<string, Set<string>>();

  origIds.forEach((origId) => indegree.set(origId, 0));

  edges.forEach((edge) => {
    if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) return;
    addToMapSet(outgoing, edge.source, edge.target);
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
  });

  const compareByTreeOrder = (a: string, b: string) =>
    (treeOrderByOrigId.get(a) ?? Number.MAX_SAFE_INTEGER) -
    (treeOrderByOrigId.get(b) ?? Number.MAX_SAFE_INTEGER);

  const ready = origIds.filter((origId) => indegree.get(origId) === 0);
  ready.sort(compareByTreeOrder);

  const ordered: string[] = [];
  while (ready.length > 0) {
    const current = ready.shift() as string;
    ordered.push(current);

    const next = Array.from(outgoing.get(current) ?? []).sort(
      compareByTreeOrder
    );
    next.forEach((target) => {
      indegree.set(target, (indegree.get(target) ?? 0) - 1);
      if (indegree.get(target) === 0) {
        ready.push(target);
        ready.sort(compareByTreeOrder);
      }
    });
  }

  const orderedSet = new Set(ordered);
  origIds
    .filter((origId) => !orderedSet.has(origId))
    .sort(compareByTreeOrder)
    .forEach((origId) => ordered.push(origId));

  return new Map(ordered.map((origId, index) => [origId, index]));
};

const buildGraphOrderContext = (
  positionedSteps: Array<any>,
  edges: Array<EdgeLike>
): GraphOrderContext => {
  const { allSteps, origIdByAlias, treeOrderByOrigId } =
    collectGraphContext(positionedSteps);
  const normalizedEdges = normalizeEdges(edges, origIdByAlias);
  const graphOrderByOrigId = getGraphOrder(
    allSteps.map((step) => normalizeKey(step.origId)).filter(Boolean),
    normalizedEdges,
    treeOrderByOrigId
  );

  return { graphOrderByOrigId, treeOrderByOrigId };
};

const sortOutputItemsWithGraphOrder = <T extends { origId?: any }>(
  items: T[],
  { graphOrderByOrigId, treeOrderByOrigId }: GraphOrderContext
): T[] =>
  [...items].sort((a, b) => {
    const aOrigId = normalizeKey(a?.origId);
    const bOrigId = normalizeKey(b?.origId);
    const aOrder = graphOrderByOrigId.get(aOrigId) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = graphOrderByOrigId.get(bOrigId) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (
      (treeOrderByOrigId.get(aOrigId) ?? Number.MAX_SAFE_INTEGER) -
      (treeOrderByOrigId.get(bOrigId) ?? Number.MAX_SAFE_INTEGER)
    );
  });

const isLoopOutputAllowed = (
  currentStep: any,
  outputStep: any,
  steps: any[],
  loopStackCache: Map<string, string[]>
): boolean => {
  if (!currentStep || !outputStep) return true;

  const getCachedLoopStack = (step: any): string[] => {
    const key = normalizeKey(step?.uniqueId ?? step?.id ?? step?.origId);
    if (key && loopStackCache.has(key)) {
      return loopStackCache.get(key) as string[];
    }

    const stack = getLoopStackForStep(step, steps);
    if (key) loopStackCache.set(key, stack);
    return stack;
  };

  const currentStack = getCachedLoopStack(currentStep);
  let outputStack = getCachedLoopStack(outputStep);
  const outputOrigId = normalizeKey(outputStep?.origId);
  if (
    (checkIsLoop(outputStep) || outputStep?.type === "LOOP") &&
    outputOrigId
  ) {
    outputStack = [...outputStack, outputOrigId];
  }
  if (outputStack.length > currentStack.length) return false;

  for (let i = 0; i < outputStack.length; i += 1) {
    if (outputStack[i] !== currentStack[i]) return false;
  }

  return true;
};

const isRouteOutputAllowed = (
  candidateOrigId: string,
  consumerOrigId: string,
  routeReachByOrigId: Map<string, Map<string, RouteReach>>
): boolean => {
  const candidateReachByRouter = routeReachByOrigId.get(candidateOrigId);
  if (!candidateReachByRouter) return true;

  return Array.from(candidateReachByRouter).every(
    ([routerOrigId, candidateReach]) => {
      const consumerReach = routeReachByOrigId
        .get(consumerOrigId)
        ?.get(routerOrigId);
      if (!consumerReach) return false;

      return isSubset(
        consumerReach.reachedBranchTokens,
        candidateReach.reachedBranchTokens
      );
    }
  );
};

export function computeAvailableOutput(
  positionedSteps: Array<any>,
  edges: Array<EdgeLike> = []
): Array<any> {
  const { allSteps, stepByOrigId, origIdByAlias, treeOrderByOrigId, routers } =
    collectGraphContext(positionedSteps);
  const normalizedEdges = normalizeEdges(edges, origIdByAlias);
  const { forward, reverse } = buildAdjacency(normalizedEdges);
  const getAncestorOrigIds = getAncestorOrigIdsFactory(reverse);
  const routeReachByOrigId = buildRouteReachByOrigId({
    routers,
    edges: normalizedEdges,
    forward,
    stepByOrigId,
  });
  const boundarySourcesByTarget = buildBoundarySourcesByTarget(normalizedEdges);
  const graphOrderByOrigId = getGraphOrder(
    allSteps.map((step) => normalizeKey(step.origId)).filter(Boolean),
    normalizedEdges,
    treeOrderByOrigId
  );
  const loopStackCache = new Map<string, string[]>();
  graphOrderContextBySteps.set(positionedSteps, {
    edges,
    context: { graphOrderByOrigId, treeOrderByOrigId },
  });

  const buildAvailableReferences = (step: any, outputKey: OutputKey) => {
    const stepOrigId = normalizeKey(step?.origId);
    if (!stepOrigId) return [];

    const ignoredBoundarySources = getBoundaryDirectSourcesToIgnore(
      stepOrigId,
      getAncestorOrigIds,
      boundarySourcesByTarget
    );

    return Array.from(getAncestorOrigIds(stepOrigId))
      .map((ancestorOrigId) => stepByOrigId.get(ancestorOrigId))
      .filter((candidate) => {
        const candidateOrigId = normalizeKey(candidate?.origId);
        if (!candidate || !candidateOrigId || candidate === step) return false;
        if (!candidate[outputKey]) return false;
        if (ignoredBoundarySources.has(candidateOrigId)) return false;
        if (
          !isLoopOutputAllowed(step, candidate, positionedSteps, loopStackCache)
        ) {
          return false;
        }
        return isRouteOutputAllowed(
          candidateOrigId,
          stepOrigId,
          routeReachByOrigId
        );
      })
      .sort((a, b) => {
        const aOrder =
          graphOrderByOrigId.get(normalizeKey(a?.origId)) ??
          Number.MAX_SAFE_INTEGER;
        const bOrder =
          graphOrderByOrigId.get(normalizeKey(b?.origId)) ??
          Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (
          (treeOrderByOrigId.get(normalizeKey(a?.origId)) ??
            Number.MAX_SAFE_INTEGER) -
          (treeOrderByOrigId.get(normalizeKey(b?.origId)) ??
            Number.MAX_SAFE_INTEGER)
        );
      })
      .map((candidate) => ({
        uniqueId: candidate.uniqueId,
        id: candidate.id,
      }));
  };

  allSteps.forEach((step) => {
    step.availableOutput = buildAvailableReferences(step, "output");
    step.availableMetaoutput = buildAvailableReferences(step, "metaoutput");
  });

  return positionedSteps;
}

export function sortOutputItemsByGraphOrder<T extends { origId?: any }>(
  items: T[],
  positionedSteps: Array<any>,
  edges: Array<EdgeLike> = []
): T[] {
  const cached = graphOrderContextBySteps.get(positionedSteps);
  const context =
    cached?.edges === edges
      ? cached.context
      : buildGraphOrderContext(positionedSteps, edges);

  return sortOutputItemsWithGraphOrder(items, context);
}
